import { Link } from 'expo-router';
import { useFormContext, useWatch } from 'react-hook-form';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import type { EventScorekeepingPanelFormValues } from '../../EventScorekeeping.types';
import EventScorekeepingPanelTeamItem, {
  EventScorekeepingPanelTeamItemFragment,
} from './EventScorekeepingPanelTeamItem';
import EventScorekeepingPanelTeamPeople, {
  EventScorekeepingPanelTeamPeopleFragment,
} from './EventScorekeepingPanelTeamPeople';

export const EventScorekeepingPanelTeamFragment = graphql(/* GraphQL */ `
  fragment EventScorekeepingPanelTeam on event {
    ...EventScorekeepingPanelTeamPeople
    event_teams(order_by: { is_home: asc }, limit: 2) {
      event_id
      team_id
      team {
        ...EventScorekeepingPanelTeamItem
        permissions
      }
    }
  }
`, [
  EventScorekeepingPanelTeamItemFragment,
  EventScorekeepingPanelTeamPeopleFragment,
]);

function EventScorekeepingPanelTeam({
  fragment,
}: {
  fragment:
    | FragmentOf<typeof EventScorekeepingPanelTeamFragment>
    | null
    | undefined;
}) {
  const {
    getValues,
    control,
    setValue,
    reset,
    formState: { defaultValues },
  } = useFormContext<EventScorekeepingPanelFormValues>();

  const event = readFragment(EventScorekeepingPanelTeamFragment, fragment);

  const selectedTeam = useWatch({
    control,
    name: 'selectedTeam',
  });

  const isEditing = useWatch({
    control,
    name: 'isEditing',
  });

  const permissions = event?.event_teams.find(
    (eventTeam) => eventTeam.team_id === selectedTeam?.id,
  )?.team.permissions;

  return (
    <Box p="md" flex direction="horizontal" alignItems="flex-start" space="md">
      <Box>
        <Box space="xs">
          {event?.event_teams.map((eventTeam) => {
            return (
              <EventScorekeepingPanelTeamItem
                key={eventTeam.team_id}
                fragment={eventTeam.team}
                isChecked={eventTeam.team_id === selectedTeam?.id}
                setValue={setValue}
              />
            );
          })}
        </Box>
      </Box>
      {selectedTeam?.id ? (
        <EventScorekeepingPanelTeamPeople
          fragment={event}
          teamId={selectedTeam.id}
        />
      ) : null}
      <Box space="md">
        {permissions?.create_invite ? (
          <Link
            href={{
              pathname: '/team/[teamId]/person/add',
              params: { teamId: selectedTeam?.id },
            }}
            asChild
          >
            <ActionButton
              variant="success"
              label="Add Members"
              icon="person-add-outline"
            />
          </Link>
        ) : null}
        {permissions?.edit_others ? (
          <ActionButton
            variant={isEditing ? 'warning' : 'info'}
            label={isEditing ? 'Stop Editing' : 'Edit Numbers'}
            icon={isEditing ? 'close-outline' : 'create-outline'}
            onPress={() => {
              reset(
                {
                  ...defaultValues,
                  selectedTeam: getValues('selectedTeam'),
                  isEditing: !isEditing,
                },
                { keepDefaultValues: true },
              );
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
}

export default EventScorekeepingPanelTeam;
